import styled from "@emotion/styled";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";
import { useLiveCharts } from "../stores/live-charts";
import { useChartCatchUp } from "../hooks/useChartCatchUp";

export interface LiveToggleProps {
  /**
   * The head block the page's own data knows about. Compared against the head the socket has
   * announced to say how stale the view is; omit it and the badge simply never shows.
   */
  knownHead?: number | null;
}

/**
 * Live-follow switch for the analytics charts, plus the catch-up affordance that makes pausing
 * safe: while paused the badge says how far behind the view has fallen, and clicking it refetches
 * once without turning live back on. Without that, a paused page is indistinguishable from a
 * stalled one.
 */
export function LiveToggle({ knownHead }: LiveToggleProps) {
  const live = useLiveCharts((s) => s.live);
  const chainHead = useLiveCharts((s) => s.chainHead);
  const setLive = useLiveCharts((s) => s.setLive);
  const catchUp = useChartCatchUp();

  const behind = chainHead != null && knownHead != null ? chainHead - knownHead : 0;

  return (
    <Wrapper>
      {!live && behind > 0 && (
        <BehindBadge
          onClick={catchUp}
          title="Blocks indexed since these charts were last updated — click to refresh"
        >
          +{behind} block{behind === 1 ? "" : "s"}
        </BehindBadge>
      )}
      <ToggleButton
        active={live}
        aria-pressed={live}
        onClick={() => setLive(!live)}
        title={
          live
            ? "Following new blocks — click to hold the charts still"
            : "Charts held still — click to follow new blocks as they land"
        }
      >
        <Dot active={live} />
        {live ? "Live" : "Paused"}
      </ToggleButton>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const ToggleButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  background: ${theme.colors.bgCard};
  color: ${(p) => (p.active ? theme.colors.text : theme.colors.textMuted)};
  border: 1px solid ${(p) => (p.active ? theme.colors.success : theme.colors.border)};
  border-radius: ${theme.radius.sm};
  cursor: pointer;
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  line-height: 1;
  transition:
    color 0.15s,
    background 0.15s,
    border-color 0.15s;
  &:hover {
    background: ${theme.colors.bgHover};
    color: ${theme.colors.text};
  }
`;

const Dot = styled.span<{ active: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => (p.active ? theme.colors.success : theme.colors.textMuted)};
`;

/**
 * Hidden on a phone, where the toolbar is already at its width budget. The toggle itself stays —
 * it is the control — but the counter is information the reader can live without there.
 */
const BehindBadge = styled.button`
  padding: 5px 8px;
  background: ${theme.colors.primary}1a;
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary}55;
  border-radius: ${theme.radius.sm};
  cursor: pointer;
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  line-height: 1;
  white-space: nowrap;
  &:hover {
    background: ${theme.colors.primary}33;
  }

  ${media.md} {
    display: none;
  }
`;
